import { useEffect, useState } from 'react';
import { Typography, Box, Button, Tooltip } from '@mui/material';
import DateFilter from '../../../../../components/shared/filters/DateFilter';
import SearchInput from '../../../../../components/shared/fields/SearchInput';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import { CloseRounded } from '@mui/icons-material';
import ResourcePlacesFilter from '../../../../../components/shared/filters/ResourcePlacesFilter';
import FormsFilter from '../../../../../components/shared/filters/FormsFilter';
import ProjectsFilter from '../../../../../components/shared/filters/Projects';
import WorkAssigneeFilter from '../../../../../components/shared/filters/WorkAssigneeFilter';
import OrderByFilter from '../../../../../components/shared/filters/OrderByFilter';
import OrderingFilter from '../../../../../components/shared/filters/OrderingFilter';
import WorksFilterByFormID from '../../../../../components/shared/filters/WorksFilterByFormID';
import { getEmptyFilters, getInitialFilters, hasFilterData } from '../../../../helpers/filtersHelpers';

const Filters = ({ applyFilterCallback, accountId, dateFilterRange }) => {
    const [baseFilters, setBaseFilters] = useState(() => {
        const defaults = getInitialFilters();
        return dateFilterRange
            ? { ...defaults, dueDateRangeTime: dateFilterRange }
            : defaults;
    });
    const [clearFilter, setClearFilter] = useState(false);
    const [filterChanged, setFilterChanged] = useState(false);
    const [selectedForms, setSelectedForms] = useState([]);

    const hasFormsSelected = baseFilters.formIds?.length > 0;
    const canClearFilter = filterChanged || hasFilterData(baseFilters);

    const handleChange = (inputName, data) => {
        setClearFilter(false);
        setBaseFilters((prev) => ({
            ...prev,
            [inputName]: data,
        }));
        setFilterChanged(true);
    };

    const clickClearButton = () => {
        setBaseFilters(getEmptyFilters());
        applyFilterCallback(getEmptyFilters());
        setClearFilter(true);
        setFilterChanged(false);
    };

    const clickApplyFilter = () => {
        setClearFilter(false);
        applyFilterCallback(baseFilters);
        setFilterChanged(false);
    };

    return (
        <>
            <Typography variant="h7" color={'text.secondary'}> Filtros </Typography>
            <Box sx={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <FormsFilter
                    clearFilter={clearFilter}
                    onChange={(data) => {
                        handleChange('formIds', data.ids);
                        setSelectedForms(data.options);
                    }}
                    accountId={accountId}
                    selectedIds={baseFilters.formIds}
                />
                <ResourcePlacesFilter
                    clearFilter={clearFilter}
                    onChange={(data) => handleChange('resourcePlaceIds', data)}
                    accountId={accountId}
                    selectedIds={baseFilters.resourcePlaceIds}
                />
                <Tooltip arrow title={hasFormsSelected ? '' : 'Selecione um formulário para habilitar este filtro'}>
                    <span>
                        <WorksFilterByFormID
                            clearFilter={clearFilter}
                            onChange={(data) => handleChange('workIds', data)}
                            accountId={accountId}
                            disabled={!hasFormsSelected}
                            formIds={baseFilters.formIds}
                            selectedIds={baseFilters.workIds}
                        />
                    </span>
                </Tooltip>
                <DateFilter
                    label="Data"
                    placeholder="-"
                    clearFilter={clearFilter}
                    onChange={(data) => handleChange('dueDateRangeTime', data)}
                    sx={{ width: '270px' }}
                    value={baseFilters.dueDateRangeTime}
                />
                <ProjectsFilter
                    clearFilter={clearFilter}
                    onChange={(data) => handleChange('projectIds', data)}
                    accountId={accountId}
                    selectedIds={baseFilters.projectIds}
                />
                <WorkAssigneeFilter
                    clearFilter={clearFilter}
                    onChange={(data) => handleChange('workAssigneeIds', data)}
                    accountId={accountId}
                    selectedIds={baseFilters.workAssigneeIds}
                />
                <Tooltip arrow title={hasFormsSelected ? '' : 'Selecione um formulário para habilitar este filtro'}>
                    <span>
                        <OrderByFilter
                            clearFilter={clearFilter}
                            onChange={(data) => handleChange('orderBy', data)}
                            formIds={baseFilters.formIds}
                            formOptions={selectedForms}
                            disabled={!hasFormsSelected}
                            accountId={accountId}
                            value={baseFilters.orderBy}
                        />
                    </span>
                </Tooltip>
                <OrderingFilter
                    clearFilter={clearFilter}
                    onChange={(data) => handleChange('ordering', data)}
                    accountId={accountId}
                    value={baseFilters.ordering}
                />
                <SearchInput
                    placeholder="Digite para pesquisar"
                    variant="outlined"
                    clearFilter={clearFilter}
                    onChange={(data) => handleChange('q', data)}
                    value={baseFilters.q}
                />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', verticalAlign: 'middle', gap: '1.5rem' }}>
                <Button
                    variant="outlined"
                    color="primary"
                    size="medium"
                    startIcon={<CheckCircleOutlineRounded />}
                    disabled={!filterChanged}
                    onClick={clickApplyFilter}
                >
                    Aplicar filtros
                </Button>
                <Button
                    variant="outlined"
                    color="warning"
                    size="medium"
                    startIcon={<CloseRounded />}
                    onClick={clickClearButton}
                    disabled={!canClearFilter}
                >
                    Limpar filtros
                </Button>
            </Box>
        </>
    );
};

export default Filters;